import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle } from "react-leaflet";
import { MagnifyingGlass, X, Crosshair, Gauge, MapPin, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, GEORGETOWN } from "@/lib/api";
import { useAuth, loginWithGoogle } from "@/lib/auth";

// Known Guyana hubs for the quick-access chips (kept short so the row fits).
const CITIES = [
  { name: "Georgetown", lat: 6.8013, lng: -58.1551 },
  { name: "New Amsterdam", lat: 6.2486, lng: -57.5239 },
  { name: "Linden", lat: 6.0067, lng: -58.3022 },
  { name: "Anna Regina", lat: 7.2667, lng: -58.5167 },
  { name: "Bartica", lat: 6.4000, lng: -58.6167 },
  { name: "Lethem", lat: 3.3833, lng: -59.8000 },
];
const NEARBY_RADIUS_KM = 5;

function haversineKm(a, b) {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const lat1 = (a.lat * Math.PI) / 180;
  const lat2 = (b.lat * Math.PI) / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// Find the densest 5km grid bucket of drivers — used for initial auto-zoom.
function densestCluster(drivers) {
  if (!drivers.length) return null;
  const buckets = new Map();
  for (const d of drivers) {
    const key = `${Math.round(d.lat / 0.05)}|${Math.round(d.lng / 0.05)}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(d);
  }
  let best = null;
  for (const list of buckets.values()) {
    if (!best || list.length > best.length) best = list;
  }
  const lat = best.reduce((s, d) => s + d.lat, 0) / best.length;
  const lng = best.reduce((s, d) => s + d.lng, 0) / best.length;
  // Find the closest known city to label this cluster
  let label = "Guyana";
  let bestDist = Infinity;
  for (const c of CITIES) {
    const d = haversineKm({ lat, lng }, c);
    if (d < bestDist) { bestDist = d; label = c.name; }
  }
  return { lat, lng, count: best.length, label };
}

function buildAvatarIcon({ picture, initial, occupancy, vehicle_color, speed_kmh, speed_hidden }) {
  const photoInner = picture
    ? `<img src="${picture}" alt="" referrerpolicy="no-referrer"
         style="width:100%;height:100%;object-fit:cover;display:block;"
         onerror="this.style.display='none';this.parentNode.dataset.fallback=1;" />
       <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#111;
         display:none;" class="avatar-fallback">${initial || "D"}</div>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;
         font-family:'DM Sans',sans-serif;font-weight:700;color:#fff;background:#111;font-size:14px;">
         ${initial || "D"}
       </div>`;

  // Rotation slots: photo → (occupancy?) → (speed?). We build up to 3 layers
  // and pick the correct set of keyframes so the DOM animation matches
  // however many "faces" this driver has to show.
  const showSpeed = !speed_hidden && typeof speed_kmh === "number" && speed_kmh > 0;
  const showOcc = !!occupancy;
  const totalFrames = 1 + (showOcc ? 1 : 0) + (showSpeed ? 1 : 0);
  const cycleDur = totalFrames === 3 ? "6s" : totalFrames === 2 ? "4s" : "0s";
  const slotAnim = (idx) => {
    if (totalFrames < 2) return "";
    return `animation:ridegy-slot-${totalFrames}-${idx} ${cycleDur} infinite;`;
  };

  const photoLayer = `
    <div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
      border:3px solid #00A859;background:#fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);
      ${slotAnim(0)}">
      ${photoInner}
    </div>`;
  const occLayer = showOcc ? buildOccupancyLayer(occupancy, slotAnim(1)) : "";
  const speedLayer = showSpeed
    ? buildSpeedLayer(speed_kmh, slotAnim(showOcc ? 2 : 1))
    : "";

  const colorHex = vehicleColorHex(vehicle_color);
  const colorDot = colorHex
    ? `<div style="position:absolute;bottom:-2px;right:-2px;width:12px;height:12px;background:${colorHex};
         border:2px solid #fff;border-radius:9999px;box-shadow:0 1px 2px rgba(0,0,0,0.4);"
         title="${vehicle_color || ""}"></div>`
    : "";
  return L.divIcon({
    className: "",
    html: `
      <div style="position:relative;width:40px;height:40px;">
        ${photoLayer}
        ${occLayer}
        ${speedLayer}
        <div style="position:absolute;top:-2px;right:-2px;width:12px;height:12px;background:#00A859;
          border:2px solid #fff;border-radius:9999px;"></div>
        ${colorDot}
      </div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

// Map common car-color names → hex swatches. Anything we don't recognise
// (empty, unusual descriptors, foreign language) returns null so we
// simply skip the swatch dot rather than showing a random colour.
function vehicleColorHex(name) {
  if (!name) return null;
  const n = String(name).trim().toLowerCase();
  const m = {
    white: "#f8fafc", black: "#111827", silver: "#c0c4cc", grey: "#6b7280", gray: "#6b7280",
    red: "#dc2626", maroon: "#7f1d1d", pink: "#ec4899",
    orange: "#f97316", yellow: "#eab308", gold: "#d4af37", cream: "#fef3c7", beige: "#e5d7b0",
    green: "#16a34a", lime: "#84cc16", teal: "#14b8a6",
    blue: "#2563eb", navy: "#1e3a8a", "sky blue": "#38bdf8", "light blue": "#60a5fa", "dark blue": "#1e3a8a",
    purple: "#7c3aed", violet: "#8b5cf6",
    brown: "#8b5a2b", bronze: "#a97142", tan: "#c8a26a",
  };
  if (m[n]) return m[n];
  // Try last-word match (e.g. "metallic silver" → "silver")
  const parts = n.split(/\s+/);
  const last = parts[parts.length - 1];
  if (m[last]) return m[last];
  return null;
}

// Occupancy visual: solid disc with white text.
// Same size as the avatar so it can overlay perfectly. The rotator
// animation is passed in (ridegy-slot-*) so it lines up with photo/speed
// layer timing.
function buildOccupancyLayer(occ, animation = "") {
  const cfg = {
    empty: { bg: "#00A859", label: "0",     sub: "EMPTY" },
    "1":   { bg: "#1d4ed8", label: "1",     sub: "PAX" },
    "2":   { bg: "#1d4ed8", label: "2",     sub: "PAX" },
    "3":   { bg: "#FF8800", label: "3",     sub: "PAX" },
    "4":   { bg: "#FF8800", label: "4",     sub: "PAX" },
    full:  { bg: "#E11D48", label: "FULL",  sub: "" },
  }[occ] || null;
  if (!cfg) return "";
  const isWord = cfg.label.length > 2;
  return `
    <div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
      border:3px solid ${cfg.bg};background:${cfg.bg};color:#fff;
      display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;
      font-family:'DM Sans',sans-serif;font-weight:800;text-align:center;
      ${animation}">
      <div style="font-size:${isWord ? 11 : 18}px;letter-spacing:0.02em;">${cfg.label}</div>
      ${cfg.sub ? `<div style="font-size:7px;font-weight:700;opacity:0.85;margin-top:1px;">${cfg.sub}</div>` : ""}
    </div>`;
}

// Speed visual: dark disc with the live km/h reading. Same 40px footprint
// so it overlays the photo/occupancy layers cleanly. Only rendered when
// the driver is actually moving (speed_kmh > 0) and speed sharing isn't
// hidden by their privacy setting.
function buildSpeedLayer(kmh, animation = "") {
  const rounded = Math.round(kmh);
  const isBig = rounded >= 100; // shrink font when 3 digits
  return `
    <div style="position:absolute;inset:0;border-radius:9999px;overflow:hidden;
      border:3px solid #0f172a;background:#0f172a;color:#fff;
      display:flex;flex-direction:column;align-items:center;justify-content:center;line-height:1;
      font-family:'DM Sans',sans-serif;font-weight:800;text-align:center;
      ${animation}">
      <div style="font-size:${isBig ? 14 : 18}px;letter-spacing:0.01em;">${rounded}</div>
      <div style="font-size:7px;font-weight:700;opacity:0.85;margin-top:1px;letter-spacing:0.05em;">KM/H</div>
    </div>`;
}

// Initial framing: Georgetown ↔ New Amsterdam corridor. This is where the
// vast majority of daily rides happen, so users see the busiest stretch
// of the coast on load. Chip clicks or searches override with flyTo.
const INITIAL_BOUNDS = [
  [6.15, -58.35], // south-west: below New Amsterdam, west of Georgetown
  [6.90, -57.40], // north-east: above Georgetown, east of New Amsterdam
];

// Inner component that has access to the leaflet map instance.
// Auto-frames the Georgetown ↔ New Amsterdam corridor on mount and
// flies to any user-selected focus (chip click or search result).
function MapController({ focus }) {
  const map = useMap();
  const didInitialFrameRef = useRef(false);

  // One-time initial framing on mount.
  useEffect(() => {
    if (!didInitialFrameRef.current) {
      didInitialFrameRef.current = true;
      map.fitBounds(INITIAL_BOUNDS, { padding: [30, 30], animate: false });
    }
  }, [map]);

  // User-driven focus changes (chip click or search result).
  useEffect(() => {
    if (focus) {
      map.flyTo([focus.lat, focus.lng], 13, { duration: 1.0 });
    }
  }, [focus, map]);

  return null;
}

async function geocodeGuyana(query) {
  // Nominatim free-tier — bias to Guyana via countrycodes.
  const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&countrycodes=gy&limit=1`;
  try {
    const r = await fetch(url, { headers: { "Accept": "application/json" } });
    if (!r.ok) return null;
    const data = await r.json();
    if (!data.length) return null;
    return {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon),
      name: data[0].display_name?.split(",").slice(0, 2).join(", ") || query,
    };
  } catch {
    return null;
  }
}

export default function PublicLiveMap({ className = "h-full w-full" }) {
  const [drivers, setDrivers] = useState([]);
  const [focus, setFocus] = useState(null); // {lat,lng,name}
  const [search, setSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const { user } = useAuth();
  const navigate = useNavigate();

  // Icon cache — keyed by the driver's visual identity signature
  // (picture URL + initial + occupancy + vehicle color + speed bucket).
  // Speed is bucketed to nearest 5 km/h so the cache doesn't invalidate
  // (and restart the rotator animation) on every 4s poll while a driver
  // is cruising.
  const iconCacheRef = useRef(new Map());
  const iconFor = (d) => {
    const speedShown = !d.speed_hidden && typeof d.speed_kmh === "number" && d.speed_kmh > 0;
    const speedBucket = speedShown ? Math.round(d.speed_kmh / 5) * 5 : 0;
    const sig = `${d.picture || ""}|${d.initial || ""}|${d.occupancy || ""}|${d.vehicle_color || ""}|${speedBucket}|${d.speed_hidden ? "h" : ""}`;
    const cache = iconCacheRef.current;
    let icon = cache.get(sig);
    if (!icon) {
      icon = buildAvatarIcon({
        picture: d.picture,
        initial: d.initial,
        occupancy: d.occupancy,
        vehicle_color: d.vehicle_color,
        speed_kmh: speedShown ? speedBucket : 0,
        speed_hidden: d.speed_hidden,
      });
      cache.set(sig, icon);
    }
    return icon;
  };

  const handleViewProfile = (driverId) => {
    if (user) {
      navigate(`/d/${driverId}`);
      return;
    }
    // Anonymous visitor — stash the intent, prompt sign-in.
    try {
      sessionStorage.setItem("ridegy:post_signin_redirect", `/d/${driverId}`);
    } catch {
      /* sessionStorage may be unavailable in private mode */
    }
    toast.message("Sign in to view driver profiles", {
      description: "Continue with Google — we'll bring you right back.",
    });
    setTimeout(() => loginWithGoogle(), 350);
  };

  // Poll the public drivers endpoint.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/public/drivers-on-map");
        if (!cancelled) setDrivers(data || []);
      } catch (_e) { /* polling tolerates intermittent failures */ }
    };
    load();
    const t = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  // Status caption: how many drivers near current focus (or densest cluster).
  const status = useMemo(() => {
    const ref = focus || densestCluster(drivers) || { lat: GEORGETOWN.lat, lng: GEORGETOWN.lng, label: "Guyana" };
    const label = focus?.name || ref.label || "Guyana";
    const nearby = drivers.filter((d) => haversineKm({ lat: d.lat, lng: d.lng }, ref) <= NEARBY_RADIUS_KM).length;
    return { label, nearby, total: drivers.length };
  }, [drivers, focus]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const q = search.trim();
    if (!q) return;
    setSearching(true);
    setSearchError("");
    // Priority 1: match a currently-broadcasting driver by name or plate.
    // The public map only shows drivers whose broadcast_location is on, so
    // this naturally excludes anyone who opted out of sharing their spot.
    const needle = q.toLowerCase();
    const driverHit = drivers.find((d) => {
      const name = (d.name || "").toLowerCase();
      const plate = (d.vehicle_plate || "").toLowerCase();
      return name.includes(needle) || plate.includes(needle);
    });
    if (driverHit) {
      setSearching(false);
      setFocus({
        lat: driverHit.lat,
        lng: driverHit.lng,
        name: driverHit.name || "Driver",
      });
      return;
    }
    // Priority 2: fall through to a Nominatim place search.
    const hit = await geocodeGuyana(q);
    setSearching(false);
    if (!hit) {
      setSearchError("No matching driver or place in Guyana");
      return;
    }
    setFocus(hit);
  };

  const jumpToCity = (c) => {
    setSearch("");
    setSearchError("");
    setFocus({ lat: c.lat, lng: c.lng, name: c.name });
  };

  const clearSearch = () => {
    setSearch("");
    setSearchError("");
    setFocus(null);
  };

  // Autocomplete suggestions — once the user has typed 3+ characters, show
  // up to 8 broadcasting drivers whose name or plate matches. Clicking a
  // suggestion flies the map to that driver's live location.
  const suggestions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 3) return [];
    const seen = new Set();
    const hits = [];
    for (const d of drivers) {
      if (hits.length >= 8) break;
      const name = (d.name || "").toLowerCase();
      const plate = (d.vehicle_plate || "").toLowerCase();
      if ((name && name.includes(q)) || (plate && plate.includes(q))) {
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        hits.push(d);
      }
    }
    return hits;
  }, [search, drivers]);

  const selectDriverSuggestion = (d) => {
    setSearch("");
    setSearchError("");
    setFocus({ lat: d.lat, lng: d.lng, name: d.name || "Driver" });
  };

  return (
    <div className={className} data-testid="landing-live-map">
      <MapContainer
        center={[6.52, -57.84]}
        zoom={9}
        scrollWheelZoom={false}
        zoomControl={false}
        attributionControl={false}
        style={{ width: "100%", height: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="" />

        <MapController focus={focus} />

        {focus && (
          <Circle
            center={[focus.lat, focus.lng]}
            radius={NEARBY_RADIUS_KM * 1000}
            pathOptions={{ color: "#FFB800", weight: 2, fillOpacity: 0.06 }}
          />
        )}

        {drivers.map((d) => (
          <Marker
            key={d.id}
            position={[d.lat, d.lng]}
            icon={iconFor(d)}
          >
            <Popup className="ridegy-popup" minWidth={220} closeButton={false}>
              <div data-testid={`driver-popup-${d.id}`} className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="font-display text-sm leading-tight">{d.name || "Driver"}</div>
                  {d.steady_hand && (
                    <span
                      data-testid={`driver-popup-steady-${d.id}`}
                      title="Steady Hand — 90%+ safety compliance"
                      className="inline-flex items-center justify-center w-4 h-4 bg-primary text-foreground border border-foreground"
                      aria-label="Steady Hand safety trophy"
                    >
                      🏆
                    </span>
                  )}
                  {d.speed_hidden ? (
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-wider bg-foreground/10 text-foreground/50 px-1.5 py-0.5">
                      Speed hidden
                    </span>
                  ) : d.in_traffic ? (
                    <span className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider bg-destructive text-destructive-foreground px-1.5 py-0.5" title="Slow-crawl detected (<10 km/h for >30s)">
                      <span aria-hidden>🚦</span>
                      In Traffic
                    </span>
                  ) : d.speed_kmh > 0 ? (
                    <span className="ml-auto flex items-center gap-1 font-mono text-[10px] uppercase tracking-wider bg-accent text-background px-1.5 py-0.5">
                      <Gauge size={10} weight="bold" />
                      {Math.round(d.speed_kmh)} km/h
                    </span>
                  ) : (
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-wider bg-foreground/10 text-foreground/70 px-1.5 py-0.5">
                      Idle
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-foreground/60">
                  {d.vehicle_full || d.vehicle_type} {d.vehicle_plate ? `· ${d.vehicle_plate}` : ""}
                </div>
                {d.address && (
                  <div className="flex items-start gap-1.5 text-[11px] text-foreground/80 leading-snug">
                    <MapPin size={12} weight="fill" className="mt-0.5 text-primary shrink-0" />
                    <span>{d.address}</span>
                  </div>
                )}
                <button
                  data-testid={`driver-popup-profile-${d.id}`}
                  onClick={() => handleViewProfile(d.id)}
                  className="w-full inline-flex items-center justify-between gap-2 text-[10px] uppercase font-mono tracking-[0.15em] bg-foreground text-background px-2 py-1.5 hover:bg-foreground/90"
                >
                  {user ? "View profile" : "Sign in to view profile"} <ArrowRight size={11} weight="bold" />
                </button>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>

      {/* Search bar — top, full-width on the map */}
      <form
        onSubmit={handleSubmit}
        className="absolute top-3 left-3 right-3 z-[600] flex flex-col items-stretch gap-2"
        data-testid="map-search-form"
      >
        <div className="flex-1 flex items-center gap-2 bg-background border-2 border-foreground h-10 px-3 shadow-[3px_3px_0_0_#111]">
          <MagnifyingGlass size={16} weight="bold" className="text-foreground/60 shrink-0" />
          <input
            data-testid="map-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search a place or driver in Guyana…"
            className="flex-1 bg-transparent outline-none text-sm font-medium placeholder:text-foreground/40 min-w-0"
          />
          {(search || focus) && (
            <button
              type="button"
              data-testid="map-search-clear"
              onClick={clearSearch}
              className="text-foreground/50 hover:text-foreground shrink-0"
              aria-label="Clear"
            >
              <X size={14} weight="bold" />
            </button>
          )}
          <button
            type="submit"
            data-testid="map-search-submit"
            disabled={searching || !search.trim()}
            className="text-[10px] font-mono uppercase tracking-[0.15em] bg-foreground text-background px-2 py-1 disabled:opacity-40 shrink-0"
          >
            {searching ? "…" : "Go"}
          </button>
        </div>
        {suggestions.length > 0 && (
          <ul
            data-testid="map-search-suggestions"
            className="bg-background border-2 border-foreground shadow-[3px_3px_0_0_#111] max-h-64 overflow-auto divide-y divide-foreground/10"
          >
            {suggestions.map((d) => (
              <li key={d.id}>
                <button
                  type="button"
                  data-testid={`map-search-suggestion-${d.id}`}
                  onClick={() => selectDriverSuggestion(d)}
                  className="w-full text-left px-3 py-2 hover:bg-foreground/5 flex items-center justify-between gap-3"
                >
                  <span className="flex flex-col min-w-0">
                    <span className="text-sm font-medium truncate">{d.name || "Driver"}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/60 truncate">
                      {(d.vehicle_full || d.vehicle_type || "").trim()}
                      {d.vehicle_plate ? ` · ${d.vehicle_plate}` : ""}
                    </span>
                  </span>
                  <span className="text-[10px] font-mono uppercase tracking-widest text-primary shrink-0">
                    Go
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </form>

      {/* Status banner — live count + current area */}
      <div
        data-testid="map-status-banner"
        className="absolute top-16 left-3 z-[500] bg-foreground text-background px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2 shadow-[3px_3px_0_0_#FFB800]"
      >
        <span className="inline-block w-2 h-2 bg-accent rounded-full pulse-dot" />
        <span data-testid="map-status-text">
          {status.nearby} near <span className="text-primary">{status.label}</span>
        </span>
      </div>

      {/* "No drivers" empty-state overlay */}
      {focus && status.nearby === 0 && (
        <div
          data-testid="map-empty-banner"
          className="absolute top-32 left-3 right-3 z-[500] bg-background border-2 border-foreground px-3 py-2 text-xs font-medium shadow-[3px_3px_0_0_#111]"
        >
          No drivers near <strong>{status.label}</strong> yet — broadcasting starts soon.
        </div>
      )}

      {/* Search error toast */}
      {searchError && (
        <div
          data-testid="map-search-error"
          className="absolute top-16 left-3 right-3 z-[500] bg-destructive text-destructive-foreground px-3 py-2 text-xs font-medium"
        >
          {searchError}
        </div>
      )}

      {/* Quick-access city chips */}
      <div
        data-testid="map-city-chips"
        className="absolute bottom-3 left-3 right-3 z-[500] flex gap-1.5 overflow-x-auto no-scrollbar pb-1"
      >
        {CITIES.map((c) => {
          const active = focus?.name === c.name;
          const count = drivers.filter((d) => haversineKm({ lat: d.lat, lng: d.lng }, c) <= NEARBY_RADIUS_KM).length;
          return (
            <button
              key={c.name}
              data-testid={`map-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => jumpToCity(c)}
              className={`flex items-center gap-1.5 px-2.5 h-8 font-mono text-[10px] uppercase tracking-[0.15em] border-2 border-foreground shrink-0 transition-colors ${
                active ? "bg-primary text-foreground" : "bg-background text-foreground hover:bg-secondary"
              }`}
            >
              <Crosshair size={12} weight="bold" />
              {c.name}
              {count > 0 && (
                <span className="ml-1 bg-foreground text-background px-1 leading-none text-[9px] py-0.5">{count}</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
